/**
 * Boolean formatting utilities
 */

import { FormatRegistry } from "./registry";
import type { BooleanFormat, FieldValue, FormattedValue } from "./types";

/**
 * Format boolean value with custom text
 */
export const toBoolean = (
  value: boolean | null | undefined,
  trueText: string = "Yes",
  falseText: string = "No"
): string | null => {
  if (value === null || value === undefined) return null;
  return value ? trueText : falseText;
};

/**
 * Format boolean as enabled/disabled
 */
export const toEnabledDisabled = (value: boolean | null | undefined): string | null => {
  return toBoolean(value, "Enabled", "Disabled");
};

/**
 * Format boolean as on/off
 */
export const toOnOff = (value: boolean | null | undefined): string | null => {
  return toBoolean(value, "On", "Off");
};

/**
 * Format boolean as active/inactive
 */
export const toActiveInactive = (value: boolean | null | undefined): string | null => {
  return toBoolean(value, "Active", "Inactive");
};

/**
 * Format boolean as true/false
 */
export const toTrueFalse = (value: boolean | null | undefined): string | null => {
  return toBoolean(value, "True", "False");
};

/**
 * Format boolean as checkmark/cross
 */
export const toCheckmark = (value: boolean | null | undefined): string | null => {
  return toBoolean(value, "✓", "✗");
};

/**
 * Parse various truthy/falsy values to boolean
 */
export const parseBoolean = (value: FieldValue): boolean | null => {
  if (value === null || value === undefined) return null;

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const lower = value.toLowerCase().trim();
    if (lower === "true" || lower === "yes" || lower === "1" || lower === "on" || lower === "enabled") {
      return true;
    }
    if (lower === "false" || lower === "no" || lower === "0" || lower === "off" || lower === "disabled") {
      return false;
    }
    return null;
  }

  if (typeof value === "number") {
    return value !== 0;
  }

  return null;
};

/**
 * Format boolean according to BooleanFormat specification
 */
export const formatBoolean = (value: FieldValue, format: BooleanFormat): FormattedValue => {
  if (format.hideWhenNull && (value === null || value === undefined)) return null;
  if (value == null) return format.nullText ?? null;

  const boolValue = parseBoolean(value);
  if (boolValue === null) return format.nullText ?? null;

  return toBoolean(boolValue, format.trueText ?? "Yes", format.falseText ?? "No");
};

// Register boolean formatters
FormatRegistry.register("boolean", (value, trueText, falseText) => {
  const boolValue = parseBoolean(value);
  if (boolValue !== null) {
    return toBoolean(
      boolValue,
      typeof trueText === "string" ? trueText : "Yes",
      typeof falseText === "string" ? falseText : "No"
    );
  }
  return null;
});

FormatRegistry.register("yesNo", (value) => {
  const boolValue = parseBoolean(value);
  if (boolValue !== null) {
    return toBoolean(boolValue, "Yes", "No");
  }
  return null;
});

FormatRegistry.register("enabledDisabled", (value) => {
  const boolValue = parseBoolean(value);
  if (boolValue !== null) {
    return toEnabledDisabled(boolValue);
  }
  return null;
});

FormatRegistry.register("onOff", (value) => {
  const boolValue = parseBoolean(value);
  if (boolValue !== null) {
    return toOnOff(boolValue);
  }
  return null;
});

FormatRegistry.register("activeInactive", (value) => {
  const boolValue = parseBoolean(value);
  if (boolValue !== null) {
    return toActiveInactive(boolValue);
  }
  return null;
});

FormatRegistry.register("trueFalse", (value) => {
  const boolValue = parseBoolean(value);
  if (boolValue !== null) {
    return toTrueFalse(boolValue);
  }
  return null;
});

FormatRegistry.register("checkmark", (value) => {
  const boolValue = parseBoolean(value);
  if (boolValue !== null) {
    return toCheckmark(boolValue);
  }
  return null;
});
